import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Turn any axios error (incl. FastAPI 422 lists) into a readable string.
export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => (x?.msg || "").replace(/^Value error, /, "")).filter(Boolean).join(". ") || fallback;
  return fallback;
};

// Keep phone inputs to digits, +, spaces, brackets and dashes.
export const cleanPhone = (v) => (v || "").replace(/[^0-9+()\-\s]/g, "").slice(0, 20);
api.interceptors.request.use((c) => {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  if (m) c.headers["X-CSRF-Token"] = decodeURIComponent(m[1]);
  return c;
});
// Expired/invalid admin session → back to login once (public pages just ignore).
api.interceptors.response.use(r => r, (err) => {
  const url = err.config?.url || "";
  const onAdmin = window.location.pathname.startsWith("/admin") && !window.location.pathname.startsWith("/admin/login");
  if (err.response?.status === 401 && !url.startsWith("/auth/") && onAdmin && !window.__redirectingToLogin) {
    window.__redirectingToLogin = true;
    window.location.assign("/admin/login?expired=1");
  }
  return Promise.reject(err);
});

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem("2rf_token"); // legacy token storage — sessions now live in an httpOnly cookie
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (_) { setUser(null); }
      finally { setLoading(false); }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout call failed (proceeding to clear local session):", e); }
    setUser(null);
  };

  const refresh = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; }
    catch (_) { return null; }
  };

  const ctxValue = useMemo(() => ({ user, loading, login, logout, refresh }), [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps -- login/logout/refresh only close over setters
  return <AuthCtx.Provider value={ctxValue}>{children}</AuthCtx.Provider>;
}
